import {
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import { toast } from "react-toastify";

import {
  AvailableRoles,
  ApiError,
  DirectoryInventoryGroupBy,
  type DirectoryResponse,
} from "@/apiClient";
import type { MakoAPIClient, User } from "@/apiClient";
import { getDataFromAllPages } from "@/apiUtils";
import {
  aggregateObservedSubdirectories,
  aggregatePreviewMatches,
  buildTemplatePath,
  createSegment,
  getDisplaySegmentsForTemplatePath,
  getLiteralSegmentValidationError,
  getPreviewMatchKey,
  parseTemplatePath,
  type DocumentedDirectoryRow,
  type FolderConfigSearch,
  type PathSegment,
  type SegmentComposerState,
  type TokenPickerTarget,
} from "@/components/folderConfig/folderConfig.utils";
import { userHasRole } from "@/components/navConfig";

const DIRECTORY_GROUP_QUERY_KEY = ["folder-config-grouped-inventory"];
const DIRECTORY_SUMMARY_QUERY_KEY = ["folder-config-summary"];
const DIRECTORY_DEFINITION_QUERY_KEY = ["folder-config-definitions"];
const DIRECTORY_DOCUMENTERS_QUERY_KEY = ["folder-config-documenters"];
const REAL_ASSET_TOKEN_QUERY_KEY = ["folder-config-real-assets"];
const UNCURATED_DIRECTORY_GROUP_PAGE_SIZE = 100;
const DOCUMENTED_DIRECTORY_GROUP_PAGE_SIZE = 500;
const DIRECTORY_DEFINITION_PAGE_SIZE = 500;
const REAL_ASSET_PAGE_SIZE = 500;
const PREVIEW_PAGE_SIZE = 10;
const FILE_PREVIEW_PAGE_SIZE = 8;
const REAL_ASSET_EXAMPLE_DETAIL_LIMIT = 40;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof ApiError) {
    return error.body?.detail || error.message || fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

const directoryInventorySummaryQueryOptions = (api: MakoAPIClient) =>
  queryOptions({
    queryKey: DIRECTORY_SUMMARY_QUERY_KEY,
    queryFn: () => api.dataAccess.getDirectoryInventorySummary(),
    throwOnError: true,
    refetchOnWindowFocus: false,
  });

const documentedInventoryQueryOptions = (api: MakoAPIClient) =>
  queryOptions({
    queryKey: [...DIRECTORY_GROUP_QUERY_KEY, "documented"],
    queryFn: async () =>
      getDataFromAllPages({
        fn: api.dataAccess.listDirectoryInventory.bind(api.dataAccess),
        args: {
          groupBy: DirectoryInventoryGroupBy.CANONICAL_PATH,
          curated: true,
        },
        downloadPageSize: DOCUMENTED_DIRECTORY_GROUP_PAGE_SIZE,
      }),
    throwOnError: true,
    refetchOnWindowFocus: false,
  });

const directoryDefinitionsQueryOptions = (api: MakoAPIClient) =>
  queryOptions({
    queryKey: DIRECTORY_DEFINITION_QUERY_KEY,
    queryFn: async () =>
      getDataFromAllPages({
        fn: api.dataAccess.listDirectoryDefinitions.bind(api.dataAccess),
        args: {},
        downloadPageSize: DIRECTORY_DEFINITION_PAGE_SIZE,
      }),
    throwOnError: true,
    refetchOnWindowFocus: false,
  });

const directoryDocumentersQueryOptions = (api: MakoAPIClient) =>
  queryOptions({
    queryKey: DIRECTORY_DOCUMENTERS_QUERY_KEY,
    queryFn: () => api.dataAccess.listDirectoryDataExperts(),
    throwOnError: true,
    refetchOnWindowFocus: false,
  });

export const realAssetTokenQueryOptions = (api: MakoAPIClient) =>
  queryOptions({
    queryKey: REAL_ASSET_TOKEN_QUERY_KEY,
    queryFn: async () =>
      getDataFromAllPages({
        fn: api.dataAccess.listRealAssets.bind(api.dataAccess),
        args: {},
        downloadPageSize: REAL_ASSET_PAGE_SIZE,
      }),
    throwOnError: true,
    refetchOnWindowFocus: false,
  });

const previewDirectoryMatchesQueryOptions = (
  api: MakoAPIClient,
  templatePath: string
) =>
  queryOptions({
    queryKey: ["folder-config-preview", templatePath],
    queryFn: async () => {
      try {
        return await api.dataAccess.previewDirectoryDefinition({
          requestBody: {
            template_path: templatePath,
            preview_limit: PREVIEW_PAGE_SIZE,
          },
        });
      } catch (error) {
        throw new Error(
          getApiErrorMessage(error, "Failed to preview directory template")
        );
      }
    },
    enabled: Boolean(templatePath),
    throwOnError: false,
    refetchOnWindowFocus: false,
  });

type UpdateFolderConfigSearch = (
  updater:
    | Partial<FolderConfigSearch>
    | ((prev: FolderConfigSearch) => FolderConfigSearch),
  options?: { replace?: boolean }
) => void;

export function useFolderConfigPage({
  api,
  search,
  updateSearch,
  user,
  builderRef,
}: {
  api: MakoAPIClient;
  search: FolderConfigSearch;
  updateSearch: UpdateFolderConfigSearch;
  user?: User | null;
  builderRef: RefObject<HTMLDivElement | null>;
}) {
  const canManageFolderConfigs = userHasRole(
    user ?? null,
    AvailableRoles.REAL_ASSET_CREATOR
  );
  const queryClient = useQueryClient();
  const hydratedEditingDirectoryPublicIdRef = useRef("");
  const descriptionRef = useRef("");
  const [descriptionSeed, setDescriptionSeed] = useState("");
  const [descriptionEditorResetKey, setDescriptionEditorResetKey] = useState(0);
  const [hasDescription, setHasDescription] = useState(false);
  const [allowAddendumUpload, setAllowAddendumUpload] = useState(true);
  const [selectedDataExpertUserId, setSelectedDataExpertUserId] = useState("");
  const [segmentComposer, setSegmentComposer] =
    useState<SegmentComposerState>(null);
  const [assetSearchQuery, setAssetSearchQuery] = useState("");
  const [isPreviewExampleOpen, setIsPreviewExampleOpen] = useState(false);

  const resetDescriptionDraft = useCallback((nextDescription: string) => {
    descriptionRef.current = nextDescription;
    setDescriptionSeed(nextDescription);
    setDescriptionEditorResetKey((previousValue) => previousValue + 1);
    setHasDescription(Boolean(nextDescription.trim()));
  }, []);

  const handleDescriptionDraftChange = useCallback(
    (nextDescription: string) => {
      descriptionRef.current = nextDescription;
      const nextHasDescription = Boolean(nextDescription.trim());
      setHasDescription((previousValue) =>
        previousValue === nextHasDescription
          ? previousValue
          : nextHasDescription
      );
    },
    []
  );

  const resetTransientWorkspaceUi = useCallback(
    (nextSegmentComposer: SegmentComposerState = null) => {
      setSegmentComposer(nextSegmentComposer);
      setAssetSearchQuery("");
      setIsPreviewExampleOpen(false);
    },
    []
  );

  const resetWorkspaceState = useCallback(
    ({
      manual,
      nextSegmentComposer = null,
    }: {
      manual: boolean;
      nextSegmentComposer?: SegmentComposerState;
    }) => {
      hydratedEditingDirectoryPublicIdRef.current = "";
      resetDescriptionDraft("");
      setAllowAddendumUpload(true);
      setSelectedDataExpertUserId("");
      resetTransientWorkspaceUi(nextSegmentComposer);
      updateSearch(
        (prev) => ({
          ...prev,
          templatePath: "",
          editingDirectoryPublicId: "",
          previewMatchKey: "",
          manual,
        }),
        { replace: false }
      );
    },
    [resetDescriptionDraft, resetTransientWorkspaceUi, updateSearch]
  );

  const updatePathSegments = useCallback(
    (
      updater: PathSegment[] | ((prev: PathSegment[]) => PathSegment[]),
      options?: { replace?: boolean }
    ) => {
      resetTransientWorkspaceUi();
      updateSearch((prev) => {
        const previousSegments = getDisplaySegmentsForTemplatePath(
          prev.templatePath
        );
        const nextSegments =
          typeof updater === "function" ? updater(previousSegments) : updater;

        return {
          ...prev,
          templatePath: buildTemplatePath(nextSegments),
          previewMatchKey: "",
        };
      }, options);
    },
    [resetTransientWorkspaceUi, updateSearch]
  );

  const suggestedQuery = search.suggestedQuery.trim();

  const inventorySummaryQuery = useQuery({
    ...directoryInventorySummaryQueryOptions(api),
    enabled: canManageFolderConfigs,
  });
  const uncuratedInventoryQuery = useInfiniteQuery({
    queryKey: [...DIRECTORY_GROUP_QUERY_KEY, "uncurated", suggestedQuery],
    queryFn: ({ pageParam = 1 }) =>
      api.dataAccess.listDirectoryInventory({
        groupBy: DirectoryInventoryGroupBy.CANONICAL_PATH,
        curated: false,
        query: suggestedQuery || undefined,
        page: pageParam,
        size: UNCURATED_DIRECTORY_GROUP_PAGE_SIZE,
      }),
    initialPageParam: 1,
    enabled: canManageFolderConfigs,
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage, pages) =>
      (lastPage.items || []).length === UNCURATED_DIRECTORY_GROUP_PAGE_SIZE
        ? pages.length + 1
        : undefined,
  });
  const realAssetTokensQuery = useQuery({
    ...realAssetTokenQueryOptions(api),
    enabled: canManageFolderConfigs,
  });
  const directoryDefinitionsQuery = useQuery({
    ...directoryDefinitionsQueryOptions(api),
    enabled: canManageFolderConfigs,
  });
  const directoryDocumentersQuery = useQuery({
    ...directoryDocumentersQueryOptions(api),
    enabled: canManageFolderConfigs,
  });
  const documentedInventoryQuery = useQuery({
    ...documentedInventoryQueryOptions(api),
    enabled: canManageFolderConfigs,
  });

  const uncuratedDirectoryGroups = useMemo(
    () =>
      uncuratedInventoryQuery.data?.pages.flatMap((page) => page.items ?? []) ??
      [],
    [uncuratedInventoryQuery.data]
  );

  const directoryDefinitions = useMemo(
    () => directoryDefinitionsQuery.data ?? [],
    [directoryDefinitionsQuery.data]
  );

  const realAssets = useMemo(
    () =>
      [...(realAssetTokensQuery.data ?? [])]
        .filter((asset) => asset.public_id)
        .sort(
          (a, b) =>
            a.name.localeCompare(b.name) ||
            (a.public_id || "").localeCompare(b.public_id || "")
        ),
    [realAssetTokensQuery.data]
  );

  const realAssetsById = useMemo(
    () =>
      new Map(
        realAssets
          .filter((asset) => asset.public_id)
          .map((asset) => [asset.public_id as string, asset])
      ),
    [realAssets]
  );

  const groupedInventoryByCanonicalPath = useMemo(
    () =>
      new Map(
        (documentedInventoryQuery.data ?? [])
          .filter((item) => item.canonical_path)
          .map((item) => [item.canonical_path as string, item])
      ),
    [documentedInventoryQuery.data]
  );

  const directoryUsers = useMemo(
    () =>
      [...(directoryDocumentersQuery.data ?? [])]
        .filter((directoryUser) => directoryUser?.id && directoryUser.email)
        .sort((a, b) => a.email.localeCompare(b.email)),
    [directoryDocumentersQuery.data]
  );

  const activeDirectoryUsers = useMemo(
    () =>
      directoryUsers.filter(
        (directoryUser) => directoryUser.is_active !== false
      ),
    [directoryUsers]
  );

  const usersById = useMemo(
    () =>
      new Map(
        directoryUsers.map((directoryUser) => [
          String(directoryUser.id),
          directoryUser,
        ])
      ),
    [directoryUsers]
  );

  const directoryUserOptions = useMemo(
    () =>
      activeDirectoryUsers.map((directoryUser) => ({
        value: String(directoryUser.id),
        label: directoryUser.email,
        searchText: `${directoryUser.email} ${directoryUser.id}`,
      })),
    [activeDirectoryUsers]
  );

  const selectedDataExpert = useMemo(
    () =>
      selectedDataExpertUserId
        ? usersById.get(selectedDataExpertUserId)
        : undefined,
    [selectedDataExpertUserId, usersById]
  );

  const selectedDataExpertLabel = useMemo(() => {
    if (!selectedDataExpert || selectedDataExpert.is_active !== false) {
      return undefined;
    }

    return `${selectedDataExpert.email} (inactive)`;
  }, [selectedDataExpert]);

  const documentedDirectoryRows = useMemo<DocumentedDirectoryRow[]>(
    () =>
      directoryDefinitions
        .filter((directory) => directory.active)
        .map((definition) => ({
          definition,
          inventory: groupedInventoryByCanonicalPath.get(
            definition.template_path
          ),
          curatedBy: definition.curated_by_user_id
            ? usersById.get(String(definition.curated_by_user_id))
            : undefined,
          dataExpert: definition.data_expert_user_id
            ? usersById.get(String(definition.data_expert_user_id))
            : undefined,
        }))
        .sort((a, b) =>
          a.definition.template_path.localeCompare(b.definition.template_path)
        ),
    [directoryDefinitions, groupedInventoryByCanonicalPath, usersById]
  );

  const totalConcreteDirectories =
    inventorySummaryQuery.data?.total_concrete_directories ?? 0;

  const documentedConcreteDirectories =
    inventorySummaryQuery.data?.documented_concrete_directories ?? 0;

  const filteredSuggestedDirectories = useMemo(
    () => uncuratedDirectoryGroups,
    [uncuratedDirectoryGroups]
  );

  const filteredDocumentedDirectoryRows = useMemo(() => {
    const searchTerm = search.documentedQuery.trim().toLowerCase();
    if (!searchTerm) {
      return documentedDirectoryRows;
    }

    return documentedDirectoryRows.filter((row) => {
      const curatorLabel =
        row.curatedBy?.email || row.definition.curated_by_user_id || "";
      const dataExpertLabel =
        row.dataExpert?.email || row.definition.data_expert_user_id || "";
      const searchable = [
        row.definition.template_path,
        row.definition.description || "",
        curatorLabel,
        dataExpertLabel,
      ]
        .join(" ")
        .toLowerCase();

      return searchable.includes(searchTerm);
    });
  }, [documentedDirectoryRows, search.documentedQuery]);

  const filteredRealAssets = useMemo(() => {
    const searchTerm = assetSearchQuery.trim().toLowerCase();
    if (!searchTerm) {
      return realAssets;
    }

    return realAssets.filter((asset) => {
      const searchable = `${asset.name} ${asset.type}`.toLowerCase();
      return searchable.includes(searchTerm);
    });
  }, [assetSearchQuery, realAssets]);

  const isRealAssetPickerOpen = segmentComposer?.mode === "real-asset";
  const realAssetPickerAssets = useMemo(
    () =>
      isRealAssetPickerOpen
        ? filteredRealAssets.slice(0, REAL_ASSET_EXAMPLE_DETAIL_LIMIT)
        : [],
    [filteredRealAssets, isRealAssetPickerOpen]
  );

  const realAssetDetailQueries = useQueries({
    queries: realAssetPickerAssets.map((asset) => ({
      queryKey: ["directory-documentation-real-asset-detail", asset.public_id],
      queryFn: () =>
        api.dataAccess.realAssetDetail({
          publicId: asset.public_id as string,
        }),
      enabled: Boolean(canManageFolderConfigs && asset.public_id),
      refetchOnWindowFocus: false,
      staleTime: ONE_DAY_MS,
    })),
  });

  const exampleDeploymentIdsByAssetId = useMemo(() => {
    const nextMap = new Map<string, string>();

    realAssetDetailQueries.forEach((query, index) => {
      const asset = realAssetPickerAssets[index];
      const assetPublicId = asset?.public_id;
      if (!assetPublicId || !query.data?.deployments?.length) {
        return;
      }

      const exampleDeploymentId = [...query.data.deployments].sort((a, b) => {
        const aSortValue = a.deployment_start_dt || a.external_id;
        const bSortValue = b.deployment_start_dt || b.external_id;
        return String(bSortValue).localeCompare(String(aSortValue));
      })[0]?.external_id;

      if (exampleDeploymentId) {
        nextMap.set(assetPublicId, exampleDeploymentId);
      }
    });

    return nextMap;
  }, [realAssetDetailQueries, realAssetPickerAssets]);

  const editingDirectory = useMemo(
    () =>
      (directoryDefinitionsQuery.data ?? []).find(
        (directory) => directory.public_id === search.editingDirectoryPublicId
      ) || null,
    [directoryDefinitionsQuery.data, search.editingDirectoryPublicId]
  );

  const pathSegments = useMemo(
    () => getDisplaySegmentsForTemplatePath(search.templatePath),
    [search.templatePath]
  );

  const templatePath = search.templatePath;
  const tokenPickerTarget =
    segmentComposer?.mode === "real-asset" ? segmentComposer.target : null;
  const tokenPickerSegment =
    segmentComposer?.mode === "real-asset" &&
    segmentComposer.target.mode === "replace" &&
    segmentComposer.target.index < pathSegments.length
      ? pathSegments[segmentComposer.target.index]
      : null;

  const previewDirectoryMatchesQuery = useQuery({
    ...previewDirectoryMatchesQueryOptions(api, templatePath),
    enabled: canManageFolderConfigs && Boolean(templatePath),
  });

  useEffect(() => {
    if (!search.editingDirectoryPublicId) {
      hydratedEditingDirectoryPublicIdRef.current = "";
      return;
    }

    if (!editingDirectory) {
      return;
    }

    if (
      hydratedEditingDirectoryPublicIdRef.current ===
      search.editingDirectoryPublicId
    ) {
      return;
    }

    hydratedEditingDirectoryPublicIdRef.current =
      search.editingDirectoryPublicId;
    const nextDescription = editingDirectory.description || "";
    const nextAllowAddendumUpload = Boolean(
      editingDirectory.allow_addendum_upload
    );
    const nextDataExpertUserId = String(
      editingDirectory.data_expert_user_id || ""
    );
    const nextTemplatePath = editingDirectory.template_path;
    let cancelled = false;

    queueMicrotask(() => {
      if (cancelled) {
        return;
      }

      resetDescriptionDraft(nextDescription);
      setAllowAddendumUpload(nextAllowAddendumUpload);
      setSelectedDataExpertUserId(nextDataExpertUserId);
      resetTransientWorkspaceUi();

      if (search.templatePath !== nextTemplatePath) {
        updateSearch((prev) => ({
          ...prev,
          templatePath: nextTemplatePath,
          previewMatchKey: "",
          manual: false,
        }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    editingDirectory,
    resetDescriptionDraft,
    resetTransientWorkspaceUi,
    search.editingDirectoryPublicId,
    search.templatePath,
    updateSearch,
  ]);

  const previewData = previewDirectoryMatchesQuery.data;
  const previewErrorMessage =
    previewDirectoryMatchesQuery.error instanceof Error
      ? previewDirectoryMatchesQuery.error.message
      : null;
  const previewConcreteMatches = useMemo(
    () => aggregatePreviewMatches(previewData?.preview_items ?? []),
    [previewData]
  );
  const previewStatus:
    | "already-documented"
    | "has-matches"
    | "no-matches"
    | "blank" = previewData?.is_curated
    ? "already-documented"
    : (previewData?.concrete_directory_count ?? 0) > 0
    ? "has-matches"
    : templatePath
    ? "no-matches"
    : "blank";

  useEffect(() => {
    if (previewConcreteMatches.length === 0) {
      if (search.previewMatchKey) {
        updateSearch({ previewMatchKey: "" });
      }
      return;
    }

    const hasSelectedMatch = search.previewMatchKey
      ? previewConcreteMatches.some(
          (item) => getPreviewMatchKey(item) === search.previewMatchKey
        )
      : false;

    if (!search.previewMatchKey || !hasSelectedMatch) {
      updateSearch({
        previewMatchKey: getPreviewMatchKey(previewConcreteMatches[0]),
      });
    }
  }, [previewConcreteMatches, search.previewMatchKey, updateSearch]);

  const selectedPreviewMatch = useMemo(() => {
    if (!search.previewMatchKey) {
      return null;
    }

    return (
      previewConcreteMatches.find(
        (item) => getPreviewMatchKey(item) === search.previewMatchKey
      ) || null
    );
  }, [previewConcreteMatches, search.previewMatchKey]);

  const selectedPreviewDeploymentId =
    selectedPreviewMatch?.cruise_real_asset_deployment?.public_id ?? null;

  const showNextPreviewMatch = useCallback(() => {
    if (previewConcreteMatches.length <= 1) {
      return;
    }

    const currentIndex = previewConcreteMatches.findIndex(
      (item) => getPreviewMatchKey(item) === search.previewMatchKey
    );
    const nextIndex =
      currentIndex >= 0
        ? (currentIndex + 1) % previewConcreteMatches.length
        : 0;

    updateSearch({
      previewMatchKey: getPreviewMatchKey(previewConcreteMatches[nextIndex]),
    });
  }, [previewConcreteMatches, search.previewMatchKey, updateSearch]);

  const observedFilesQuery = useQuery({
    queryKey: [
      "directory-documentation-observed-files",
      selectedPreviewDeploymentId || "",
      selectedPreviewMatch?.path || "",
    ],
    queryFn: async () => {
      const response = await api.dataAccess.searchDataAssets({
        directory: selectedPreviewMatch?.path ?? undefined,
        realAssetDeploymentPublicIds: selectedPreviewDeploymentId
          ? [selectedPreviewDeploymentId]
          : undefined,
        includeDescendantDeployments: true,
        squashByPath: true,
        sortField: "path",
        sortOrder: "asc",
        size: FILE_PREVIEW_PAGE_SIZE,
      });

      return response.items;
    },
    enabled: Boolean(selectedPreviewMatch?.path && selectedPreviewDeploymentId),
    throwOnError: false,
    refetchOnWindowFocus: false,
  });

  const observedSubdirectoriesQuery = useQuery({
    queryKey: [
      "directory-documentation-observed-subdirs",
      selectedPreviewDeploymentId || "",
      selectedPreviewMatch?.path || "",
    ],
    queryFn: async () => {
      const response = await api.dataAccess.listTreeDirectories({
        realAssetDeploymentPublicId: selectedPreviewDeploymentId as string,
        directory: selectedPreviewMatch?.path ?? undefined,
        size: FILE_PREVIEW_PAGE_SIZE,
      });
      return response.items;
    },
    enabled: Boolean(selectedPreviewMatch?.path && selectedPreviewDeploymentId),
    throwOnError: false,
    refetchOnWindowFocus: false,
  });

  const observedSubdirectories = useMemo(
    () =>
      aggregateObservedSubdirectories(observedSubdirectoriesQuery.data ?? []),
    [observedSubdirectoriesQuery.data]
  );
  const observedFiles = observedFilesQuery.data ?? [];

  const resetWorkspace = useCallback(() => {
    resetWorkspaceState({ manual: false });
  }, [resetWorkspaceState]);

  const loadDirectoryIntoWorkspace = useCallback(
    ({
      directory,
      templatePath: nextTemplatePath,
      description: nextDescription,
      allowAddendumUpload: nextAllowAddendumUpload,
      dataExpertUserId: nextDataExpertUserId,
    }: {
      directory?: DirectoryResponse | null;
      templatePath: string;
      description?: string | null;
      allowAddendumUpload?: boolean | null;
      dataExpertUserId?: string | null;
    }) => {
      const { error } = parseTemplatePath(nextTemplatePath, realAssetsById);
      if (error) {
        toast.error(error);
        return;
      }

      resetDescriptionDraft(nextDescription || "");
      setAllowAddendumUpload(Boolean(nextAllowAddendumUpload));
      setSelectedDataExpertUserId(nextDataExpertUserId || "");
      resetTransientWorkspaceUi();
      updateSearch(
        (prev) => ({
          ...prev,
          templatePath: nextTemplatePath,
          editingDirectoryPublicId: directory?.public_id || "",
          previewMatchKey: "",
          manual: false,
        }),
        { replace: false }
      );

      if (
        builderRef.current &&
        typeof builderRef.current.scrollIntoView === "function"
      ) {
        builderRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    },
    [
      builderRef,
      realAssetsById,
      resetDescriptionDraft,
      resetTransientWorkspaceUi,
      updateSearch,
    ]
  );

  const saveDirectoryMutation = useMutation({
    mutationFn: async () => {
      const requestBody = {
        template_path: templatePath,
        description: descriptionRef.current.trim(),
        allow_addendum_upload: allowAddendumUpload,
        data_expert_user_id: selectedDataExpertUserId || null,
      };

      if (editingDirectory) {
        if (!editingDirectory.public_id) {
          throw new Error(
            "The selected documentation entry is missing a public ID."
          );
        }

        return api.dataAccess.patchDirectoryDefinition({
          publicId: editingDirectory.public_id,
          requestBody,
        });
      }

      return api.dataAccess.createDirectoryDefinition({
        requestBody,
      });
    },
    onSuccess: (savedDirectory: DirectoryResponse) => {
      toast.success(
        editingDirectory ? "Changes saved." : "Directory documented!"
      );
      queryClient.invalidateQueries({ queryKey: DIRECTORY_GROUP_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: DIRECTORY_DEFINITION_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: ["folder-config-preview"],
      });
      resetDescriptionDraft(savedDirectory.description || "");
      setAllowAddendumUpload(Boolean(savedDirectory.allow_addendum_upload));
      setSelectedDataExpertUserId(
        String(savedDirectory.data_expert_user_id || "")
      );
      resetTransientWorkspaceUi();
      updateSearch((prev) => ({
        ...prev,
        editingDirectoryPublicId: savedDirectory.public_id || "",
        templatePath: savedDirectory.template_path,
        manual: false,
      }));
    },
    onError: (error: Error) => {
      toast.error(`Something went wrong: ${error.message}`);
    },
  });

  const editingPreviewPublicId = previewData?.directory?.public_id;
  const isEditingCurrentDirectory =
    Boolean(editingDirectory?.public_id) &&
    editingDirectory?.public_id === editingPreviewPublicId;
  const isDuplicateDocumentedPath =
    previewStatus === "already-documented" && !isEditingCurrentDirectory;

  const canSubmit =
    Boolean(templatePath) &&
    hasDescription &&
    !previewDirectoryMatchesQuery.isPending &&
    !previewErrorMessage &&
    !isDuplicateDocumentedPath &&
    !saveDirectoryMutation.isPending;

  const previewContentsLoading =
    observedFilesQuery.isPending || observedSubdirectoriesQuery.isPending;
  const previewContentsEmpty =
    observedSubdirectories.length === 0 &&
    observedFiles.length === 0 &&
    !observedFilesQuery.isPending &&
    !observedSubdirectoriesQuery.isPending;

  const closeSegmentComposer = useCallback(() => {
    setSegmentComposer(null);
    setAssetSearchQuery("");
  }, []);

  const toggleSegmentTypeChooser = useCallback(() => {
    setSegmentComposer((current) =>
      current?.mode === "chooser" ? null : { mode: "chooser" }
    );
    setAssetSearchQuery("");
  }, []);

  const openRealAssetPicker = useCallback((target: TokenPickerTarget) => {
    setSegmentComposer({ mode: "real-asset", target });
    setAssetSearchQuery("");
  }, []);

  const openLiteralComposer = useCallback(() => {
    setSegmentComposer({ mode: "literal", draft: "" });
    setAssetSearchQuery("");
  }, []);

  const updateLiteralComposerDraft = useCallback((value: string) => {
    setSegmentComposer((current) =>
      current?.mode === "literal" ? { ...current, draft: value } : current
    );
  }, []);

  const commitLiteralSegmentEdit = useCallback(() => {
    if (segmentComposer?.mode !== "literal") {
      return;
    }

    const validationError = getLiteralSegmentValidationError(
      segmentComposer.draft
    );
    if (validationError) {
      toast.error(validationError);
      return;
    }

    updatePathSegments((segments) => [
      ...segments,
      createSegment("literal", segmentComposer.draft.trim()),
    ]);
    closeSegmentComposer();
  }, [closeSegmentComposer, segmentComposer, updatePathSegments]);

  const insertOrReplaceTokenSegment = useCallback(
    (assetPublicId: string) => {
      if (segmentComposer?.mode !== "real-asset") {
        return;
      }

      updatePathSegments((segments) => {
        const nextSegment = createSegment("token", assetPublicId);
        const target = segmentComposer.target;

        if (target.mode === "replace" && target.index < segments.length) {
          return segments.map((segment, segmentIndex) =>
            segmentIndex === target.index ? nextSegment : segment
          );
        }

        const insertionIndex = Math.min(target.index, segments.length);
        return [
          ...segments.slice(0, insertionIndex),
          nextSegment,
          ...segments.slice(insertionIndex),
        ];
      });
      closeSegmentComposer();
    },
    [closeSegmentComposer, segmentComposer, updatePathSegments]
  );

  const commitExistingLiteralSegment = useCallback(
    (segmentIndex: number, nextValue: string) => {
      const validationError = getLiteralSegmentValidationError(nextValue);
      if (validationError) {
        toast.error(validationError);
        return false;
      }

      updatePathSegments((segments) =>
        segments.map((segment, index) =>
          index === segmentIndex
            ? { ...segment, value: nextValue.trim() }
            : segment
        )
      );
      return true;
    },
    [updatePathSegments]
  );

  const removePathSegment = useCallback(
    (segmentIndex: number) => {
      updatePathSegments((segments) =>
        segments.filter((_, index) => index !== segmentIndex)
      );
    },
    [updatePathSegments]
  );

  const handleSuggestionUse = useCallback(
    (item: { canonical_path?: string | null }) => {
      loadDirectoryIntoWorkspace({
        templatePath: item.canonical_path || "",
        description: "",
        allowAddendumUpload: true,
        dataExpertUserId: null,
      });
    },
    [loadDirectoryIntoWorkspace]
  );

  const handleEditDocumentedDirectory = useCallback(
    (row: DocumentedDirectoryRow) => {
      loadDirectoryIntoWorkspace({
        directory: row.definition,
        templatePath: row.definition.template_path,
        description: row.definition.description,
        allowAddendumUpload: row.definition.allow_addendum_upload,
        dataExpertUserId: row.definition.data_expert_user_id,
      });
    },
    [loadDirectoryIntoWorkspace]
  );

  const handleStartNewPattern = useCallback(() => {
    resetWorkspaceState({
      manual: true,
      nextSegmentComposer: { mode: "chooser" },
    });
  }, [resetWorkspaceState]);

  const documentedPatternCount =
    inventorySummaryQuery.data?.documented_pattern_count ??
    directoryDefinitions.length;
  const awaitingDocumentationCount =
    inventorySummaryQuery.data?.awaiting_documentation_count ?? 0;
  const totalSuggestedDirectoryMatches =
    uncuratedInventoryQuery.data?.pages[0]?.total ?? 0;
  const hasMoreSuggestedDirectories = Boolean(
    uncuratedInventoryQuery.hasNextPage
  );
  const isLoadingMoreSuggestedDirectories =
    uncuratedInventoryQuery.isFetchingNextPage;

  const coveragePercent =
    totalConcreteDirectories > 0
      ? Math.round(
          (documentedConcreteDirectories / totalConcreteDirectories) * 100
        )
      : 0;

  const showForm =
    pathSegments.length > 0 || editingDirectory !== null || search.manual;

  const saveButtonLabel = saveDirectoryMutation.isPending
    ? "Saving..."
    : previewDirectoryMatchesQuery.isPending
    ? "Loading..."
    : isDuplicateDocumentedPath
    ? "Already Documented"
    : editingDirectory
    ? "Save Changes"
    : "Save Documentation";

  const status = !canManageFolderConfigs
    ? "unauthorized"
    : inventorySummaryQuery.isPending ||
      realAssetTokensQuery.isPending ||
      directoryDefinitionsQuery.isPending ||
      (search.tab === "needs-docs" && uncuratedInventoryQuery.isPending) ||
      (search.tab === "documented" && documentedInventoryQuery.isPending)
    ? "loading"
    : "ready";

  return {
    status,
    stats: {
      documentedPatternCount,
      awaitingDocumentationCount,
      totalConcreteDirectories,
      documentedConcreteDirectories,
      coveragePercent,
    },
    listPanel: {
      tab: search.tab,
      suggestedQuery: search.suggestedQuery,
      documentedQuery: search.documentedQuery,
      documentedPatternCount,
      awaitingDocumentationCount,
      totalSuggestedDirectoryMatches,
      documentedDirectoryRows,
      filteredSuggestedDirectories,
      filteredDocumentedDirectoryRows,
      hasMoreSuggestedDirectories,
      isLoadingMoreSuggestedDirectories,
      templatePath,
      editingDirectoryPublicId: editingDirectory?.public_id || "",
      realAssetsById,
      onTabChange: (tab: FolderConfigSearch["tab"]) =>
        updateSearch({ tab }, { replace: false }),
      onSuggestedQueryChange: (suggestedQuery: string) =>
        updateSearch({ suggestedQuery }),
      onDocumentedQueryChange: (documentedQuery: string) =>
        updateSearch({ documentedQuery }),
      onLoadMoreSuggestedDirectories: () => {
        if (uncuratedInventoryQuery.hasNextPage) {
          void uncuratedInventoryQuery.fetchNextPage();
        }
      },
      onUseSuggestion: handleSuggestionUse,
      onEditDocumentedDirectory: handleEditDocumentedDirectory,
      onStartNewPattern: handleStartNewPattern,
    },
    editorPanel: {
      showForm,
      editingDirectory,
      templatePath,
      pathSegments,
      realAssetsById,
      filteredRealAssets,
      exampleDeploymentIdsByAssetId,
      segmentComposer,
      assetSearchQuery,
      tokenPickerTarget,
      tokenPickerSegment,
      onAssetSearchQueryChange: setAssetSearchQuery,
      onToggleSegmentTypeChooser: toggleSegmentTypeChooser,
      onOpenLiteralComposer: openLiteralComposer,
      onOpenRealAssetPicker: openRealAssetPicker,
      onCloseSegmentComposer: closeSegmentComposer,
      onLiteralComposerDraftChange: updateLiteralComposerDraft,
      onCommitLiteralSegment: commitLiteralSegmentEdit,
      onCommitExistingLiteralSegment: commitExistingLiteralSegment,
      onInsertOrReplaceTokenSegment: insertOrReplaceTokenSegment,
      onRemovePathSegment: removePathSegment,
      previewData,
      previewErrorMessage,
      previewStatus,
      previewConcreteMatches,
      selectedPreviewMatch,
      isPreviewPending: previewDirectoryMatchesQuery.isPending,
      isPreviewExampleOpen,
      onTogglePreviewExample: () =>
        setIsPreviewExampleOpen((current) => !current),
      onShowNextPreviewMatch: showNextPreviewMatch,
      observedSubdirectories,
      observedFiles,
      previewContentsLoading,
      previewContentsEmpty,
      descriptionSeed,
      descriptionEditorResetKey,
      hasDescription,
      onDescriptionChange: handleDescriptionDraftChange,
      allowAddendumUpload,
      onAllowAddendumUploadChange: setAllowAddendumUpload,
      directoryUserOptions,
      isDirectoryUsersLoading: directoryDocumentersQuery.isPending,
      selectedDataExpertUserId,
      selectedDataExpertLabel,
      onSelectedDataExpertChange: setSelectedDataExpertUserId,
      isDuplicateDocumentedPath,
      canSubmit,
      saveButtonLabel,
      onSave: () => saveDirectoryMutation.mutate(),
      onResetWorkspace: resetWorkspace,
      isEditingCurrentDirectory,
      concreteDirectoryCount: previewData?.concrete_directory_count ?? 0,
      cruiseCount: previewData?.cruise_count ?? 0,
    },
  };
}
